/**
 * Small overlay badges + toolbar buttons that ride in the corners
 * of the editor's "desk" via ScaledPaper's slot props:
 *   - DocumentStatsBadge — top-left: word count + reading time
 *   - PageNumbersToggle  — top-right: alongside zoom controls
 *
 * Both are preview-only chrome — they never render in the exported
 * .docx / .pdf. The PageNumbersToggle's value DOES affect export
 * via the `showPageNumbers` flag threaded into PagedDocument →
 * Paper, but the toggle button itself is a UI-only affordance.
 */
import type { StructuredContent } from '@ojb/shared';
import { Hash } from 'lucide-react';

/** Approximate adult reading speed for English in WPM. Used to
 *  compute the reading-time estimate. 200 is a conservative middle
 *  ground; technical reading is closer to 150, casual to 250. */
const READING_WPM = 200;

/** Counts words in the structured content — title, project,
 *  location, every attendee group's firm + member names, every
 *  action-item description + assignee, every topic title + every
 *  bullet's body + speaker. Field names match the actual
 *  StructuredContent schema (was previously using a few wrong
 *  field names like `attendees`, `text`, `assignee` which silently
 *  iterated empty/undefined and undercounted dramatically). */
export function countDocumentWords(content: StructuredContent | null): number {
  if (!content) return 0;
  const tokens: string[] = [];
  if (content.title) tokens.push(content.title);
  if (content.project) tokens.push(content.project);
  if (content.location) tokens.push(content.location);
  for (const g of content.attendeeGroups ?? []) {
    if (g.firm) tokens.push(g.firm);
    for (const m of g.members ?? []) {
      if (m) tokens.push(m);
    }
  }
  for (const ai of content.actionItems ?? []) {
    if (ai.description) tokens.push(ai.description);
    if (ai.assigneeName) tokens.push(ai.assigneeName);
  }
  for (const t of content.topics ?? []) {
    if (t.title) tokens.push(t.title);
    for (const b of t.bullets ?? []) {
      if (b.body) tokens.push(b.body);
      if (b.speaker) tokens.push(b.speaker);
    }
  }
  // One word = whitespace-delimited run of non-whitespace.
  let total = 0;
  for (const tok of tokens) {
    total += tok.split(/\s+/).filter(Boolean).length;
  }
  return total;
}

export function DocumentStatsBadge({
  content,
  pageCount,
}: {
  content: StructuredContent | null;
  /**
   * Optional page count from the editor's PaginatedPaper (Tiptap
   * path only — legacy editor manages its own pagination via
   * PagedDocument). When present and > 0, renders as a third stat
   * after words + min-read. Hidden on the legacy path so the
   * badge stays compact when nothing supplies it.
   */
  pageCount?: number;
}) {
  const words = countDocumentWords(content);
  if (words === 0) return null;
  const minutes = Math.max(1, Math.round(words / READING_WPM));
  const showPages = typeof pageCount === 'number' && pageCount > 0;
  // Mockup .doc-stats-badge: rounded-full + charcoal/15 border +
  // cloud/90 bg + 4/12 padding + 10px tabular-nums charcoal/65 +
  // backdrop-blur + soft shadow + pine bold accents. Charcoal/30
  // separators are dim enough to not compete with the pine numbers.
  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-full border border-charcoal/15 bg-cloud/90 px-3 py-1 text-[10px] tabular-nums text-charcoal/65 shadow-[0_1px_2px_rgba(43,42,47,0.06)] backdrop-blur"
      title={
        `${words.toLocaleString()} words · ~${minutes} min read at ${READING_WPM} wpm` +
        (showPages
          ? ` · ${pageCount} page${pageCount === 1 ? '' : 's'}`
          : '')
      }
    >
      <span>
        <b className="font-bold text-pine">{words.toLocaleString()}</b>
        <span className="ml-1 text-charcoal/50">words</span>
      </span>
      <span aria-hidden className="text-charcoal/30">·</span>
      <span>
        <b className="font-bold text-pine">~{minutes}</b>
        <span className="ml-1 text-charcoal/50">min read</span>
      </span>
      {showPages && (
        <>
          <span aria-hidden className="text-charcoal/30">·</span>
          <span>
            <b className="font-bold text-pine">{pageCount}</b>
            <span className="ml-1 text-charcoal/50">
              page{pageCount === 1 ? '' : 's'}
            </span>
          </span>
        </>
      )}
    </div>
  );
}

export function PageNumbersToggle({
  enabled,
  onToggle,
}: {
  enabled: boolean;
  onToggle: () => void;
}) {
  // Mockup .page-num-toggle: pine pill with cream text when ON,
  // outlined cloud-bg ghost when OFF. 11px medium, padding 5/12px.
  // Sits next to the zoom pill in ScaledPaper's toolbarExtra slot.
  return (
    <button
      type="button"
      onClick={onToggle}
      title={enabled ? 'Hide page numbers' : 'Show page numbers'}
      aria-label={enabled ? 'Hide page numbers' : 'Show page numbers'}
      className={
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium transition-all ' +
        (enabled
          ? 'border-pine bg-pine text-cream shadow-[0_1px_2px_rgba(43,42,47,0.10)] hover:bg-pine-deep'
          : 'border-charcoal/15 bg-cloud/90 text-charcoal/65 backdrop-blur hover:border-pine hover:text-pine')
      }
    >
      <Hash size={11} />
      <span>Page #</span>
    </button>
  );
}
